import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { GripVertical, Users, Download } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useContacts,
  useCreateDealForContact,
  fetchAllContactsForExport,
  type Contact,
} from '@/features/contacts/queries';
import { toCsv, downloadCsv, dateStamp } from '@/lib/csv';
import { useUpsertPreferences, useUserPreferences } from '@/features/preferences/queries';
import { useCurrentPipeline } from '@/features/preferences/use-pipeline';
import { usePipelineStages } from '@/features/pipeline/queries';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/contacts')({ component: ContactsPage });

const VERTICAL_LABEL: Record<string, string> = {
  trades: 'Trades',
  local_services: 'Local Services',
  health_fitness: 'Health & Fitness',
  hospitality: 'Hospitality',
};

type ContactsView = 'list' | 'grid';

function ContactsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [vertical, setVertical] = useState('');
  const [page, setPage] = useState(0);
  const [view, setView] = useState<ContactsView>('list');
  const [toast, setToast] = useState<string | null>(null);
  const pageSize = 50;
  const { pipeline: activePipeline } = useCurrentPipeline();
  const { data, isLoading } = useContacts({ search, vertical, page, pageSize, pipeline: activePipeline });
  const { data: stages } = usePipelineStages(activePipeline);
  const { data: prefs } = useUserPreferences();
  const upsertPrefs = useUpsertPreferences();
  const createDeal = useCreateDealForContact();
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (prefs?.contacts_view) setView(prefs.contacts_view);
  }, [prefs?.contacts_view]);

  const onViewChange = (next: ContactsView): void => {
    setView(next);
    upsertPrefs.mutate({ contacts_view: next });
  };

  const orderedRows = useMemo<Contact[]>(() => {
    if (!data?.rows) return [];
    const order = prefs?.contacts_grid_order ?? null;
    if (!order || order.length === 0) return data.rows;
    const indexFor = new Map(order.map((id, i) => [id, i]));
    const fallback = order.length;
    return [...data.rows].sort(
      (a, b) => (indexFor.get(a.id) ?? fallback) - (indexFor.get(b.id) ?? fallback),
    );
  }, [data?.rows, prefs?.contacts_grid_order]);

  const orderedIds = useMemo<string[]>(() => orderedRows.map((c) => c.id), [orderedRows]);
  const visibleIds = useMemo<string[]>(() => data?.rows.map((c) => c.id) ?? [], [data?.rows]);

  const showToast = (msg: string): void => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const [exporting, setExporting] = useState(false);
  const onExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const rows = await fetchAllContactsForExport({ search, vertical, pipeline: activePipeline });
      const csv = toCsv(rows, [
        { header: 'First Name', value: (c) => c.first_name },
        { header: 'Last Name', value: (c) => c.last_name },
        { header: 'Email', value: (c) => c.email },
        { header: 'Phone', value: (c) => c.phone },
        { header: 'Company', value: (c) => c.company },
        { header: 'Vertical', value: (c) => c.vertical },
        { header: 'Source', value: (c) => c.source },
        { header: 'City', value: (c) => c.city },
        { header: 'ICP Score', value: (c) => c.icp_score },
        {
          header: 'Created',
          value: (c) => (c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : ''),
        },
      ]);
      downloadCsv(`contacts-${activePipeline}-${dateStamp()}.csv`, csv);
      const total = data?.total ?? rows.length;
      const note = rows.length < total ? ` (capped from ${total.toLocaleString()})` : '';
      showToast(`Exported ${rows.length.toLocaleString()} contacts${note}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  const onDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);
    const activeId = String(active.id);
    if (activeId === overId) return;

    if (overId.startsWith('stage:')) {
      const stageId = overId.slice('stage:'.length);
      const stage = stages?.find((s) => s.id === stageId);
      if (!stage) return;
      createDeal.mutate(
        { contactId: activeId, stageId, pipeline: activePipeline },
        {
          onSuccess: () => showToast(`Deal created in ${stage.name}`),
          onError: (err) => showToast(`Failed: ${(err as Error).message}`),
        },
      );
      return;
    }

    if (overId.startsWith('card:')) {
      const targetId = overId.slice('card:'.length);
      const ids = orderedRows.map((c) => c.id);
      const fromIdx = ids.indexOf(activeId);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return;
      const next = [...ids];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, activeId);
      upsertPrefs.mutate({ contacts_grid_order: next });
    }
  };

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08] flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-2xl">Contacts</h1>
          {data && (
            <span className="text-xs text-text-mute font-mono px-2 py-0.5 rounded bg-surface-2">
              {data.total.toLocaleString()}
            </span>
          )}
          <div className="flex-1" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search name, email, company…"
            className="max-w-xs"
          />
          <select
            value={vertical}
            onChange={(e) => {
              setVertical(e.target.value);
              setPage(0);
            }}
            className="text-sm bg-surface-2 border border-border/[0.16] rounded px-3 py-1.5"
          >
            <option value="">All verticals</option>
            <option value="trades">Trades</option>
            <option value="local_services">Local Services</option>
            <option value="health_fitness">Health & Fitness</option>
            <option value="hospitality">Hospitality</option>
          </select>
          <div className="flex items-center rounded border border-border/[0.12] bg-surface-2 p-0.5">
            {(['list', 'grid'] as ContactsView[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onViewChange(v)}
                className={cn(
                  'rounded px-2 py-1 text-[10px] font-medium uppercase tracking-wider transition',
                  view === v ? 'bg-accent/15 text-accent' : 'text-text-mute hover:text-text-dim',
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={exporting || !data || data.total === 0}
            onClick={() => void onExport()}
            title="Export the current filtered contacts to CSV"
          >
            <Download className="mr-1 h-3 w-3" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </div>

        <div className="flex-1 overflow-auto relative">
          {isLoading ? (
            <ContactsSkeleton view={view} />
          ) : !data || data.rows.length === 0 ? (
            <EmptyState
              icon={Users}
              title={search || vertical ? 'No matching contacts' : 'No contacts yet'}
              description={
                search || vertical
                  ? 'Try a different search term or clear the vertical filter.'
                  : 'Contacts appear here as leads come in from your scrapers and inbound channels.'
              }
            />
          ) : view === 'grid' ? (
            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <div className="flex h-full">
                <div className="flex-1 overflow-auto p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {orderedRows.map((c, idx) => (
                      <ContactCard
                        key={c.id}
                        contact={c}
                        listIds={orderedIds}
                        cursor={idx}
                      />
                    ))}
                  </div>
                </div>
                <StageRail stages={stages ?? []} pipelineLabel={activePipeline} />
              </div>
            </DndContext>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg z-10">
                <tr className="text-left text-text-mute text-xs uppercase tracking-wider border-b border-border/[0.08]">
                  <th className="py-3 px-6">Name</th>
                  <th className="py-3">Email</th>
                  <th className="py-3">Company</th>
                  <th className="py-3">Vertical</th>
                  <th className="py-3">City</th>
                  <th className="py-3 text-right">ICP</th>
                  <th className="py-3 px-6 text-right">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((c, idx) => (
                  <tr key={c.id} className="border-b border-border/[0.04] hover:bg-surface-2 transition-colors">
                    <td className="py-3 px-6">
                      <Link
                        to="/contacts/$id"
                        params={{ id: c.id }}
                        state={{
                          listCursor: {
                            ids: visibleIds,
                            cursor: idx,
                            returnTo: '/contacts',
                          },
                        }}
                        className="font-medium hover:text-accent"
                      >
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                      </Link>
                    </td>
                    <td className="py-3 text-text-dim">{c.email ?? '—'}</td>
                    <td className="py-3">{c.company ?? '—'}</td>
                    <td className="py-3 text-text-dim">{c.vertical ? (VERTICAL_LABEL[c.vertical] ?? c.vertical) : '—'}</td>
                    <td className="py-3 text-text-dim">{c.city ?? '—'}</td>
                    <td className="py-3 text-right font-mono text-xs">{c.icp_score ?? '—'}</td>
                    <td className="py-3 px-6 text-right text-text-mute text-xs font-mono">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {toast && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-surface-2 border border-accent/40 px-4 py-2 text-sm shadow-lg z-20">
              {toast}
            </div>
          )}
        </div>

        {data && totalPages > 1 && (
          <div className="px-6 py-3 border-t border-border/[0.08] flex items-center justify-between text-sm">
            <span className="text-text-mute">
              Page {page + 1} of {totalPages} · {data.total.toLocaleString()} contacts
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ContactsSkeleton({ view }: { view: ContactsView }): JSX.Element {
  if (view === 'grid') {
    return (
      <div className="p-6" aria-busy="true">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border/[0.08] bg-surface-2/50 p-4">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
              <Skeleton className="mt-3 h-3 w-3/4" />
              <div className="mt-3 flex gap-1.5">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="px-6 py-4" aria-busy="true">
      <div className="space-y-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-3 w-1/5" />
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-3 w-1/6" />
            <div className="flex-1" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface ContactCardProps {
  contact: Contact;
  listIds: ReadonlyArray<string>;
  cursor: number;
}

function ContactCard({ contact, listIds, cursor }: ContactCardProps): JSX.Element {
  // Drag listeners attach ONLY to the grip handle, not the whole card.
  // The card body is a Link so click-to-detail-page is reliable.
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: contact.id });
  const drop = useDroppable({ id: `card:${contact.id}` });
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—';

  // ICP heat tint — band the score
  const icp = contact.icp_score ?? null;
  const heat: 'hot' | 'warm' | 'cold' | null =
    icp == null ? null : icp >= 70 ? 'hot' : icp >= 40 ? 'warm' : 'cold';
  const heatBg =
    heat === 'hot'
      ? 'bg-[linear-gradient(135deg,rgb(var(--icp-hot)/0.10)_0%,transparent_60%)]'
      : heat === 'warm'
        ? 'bg-[linear-gradient(135deg,rgb(var(--icp-warm)/0.08)_0%,transparent_60%)]'
        : '';
  const icpClass =
    heat === 'hot'
      ? 'text-icp-hot'
      : heat === 'warm'
        ? 'text-icp-warm'
        : 'text-icp-cold';

  return (
    <div
      ref={drop.setNodeRef}
      className={cn(
        'group relative rounded-lg border border-border/[0.08] bg-surface-2 overflow-hidden transition-[transform,border-color,box-shadow] duration-fast ease-ease',
        'hover:-translate-y-px hover:border-accent/40 hover:shadow-[0_8px_24px_-12px_rgb(var(--accent)/0.35)]',
        heatBg,
        isDragging && 'opacity-40',
        drop.isOver && 'ring-2 ring-accent/60',
      )}
    >
      {/* Drag handle — setDragRef + listeners are HERE only, so dnd-kit
          never intercepts pointer events on the rest of the card. */}
      <button
        ref={setDragRef}
        type="button"
        {...attributes}
        {...listeners}
        className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded text-text-mute opacity-0 transition-opacity hover:bg-surface-hi hover:text-text group-hover:opacity-70 cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
        title="Drag to reorder"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Click target — always a link to /contacts/$id */}
      <Link
        to="/contacts/$id"
        params={{ id: contact.id }}
        state={{
          listCursor: {
            ids: listIds as string[],
            cursor,
            returnTo: '/contacts',
          },
        }}
        className="block p-4 cursor-pointer"
      >
        <div className="flex items-start justify-between gap-2 pr-6">
          <div className="font-medium text-text truncate">{fullName}</div>
          {icp != null && (
            <span className={cn('font-mono text-[10px] font-semibold', icpClass)}>{icp}</span>
          )}
        </div>
        <div className="mt-1 truncate text-xs text-text-dim">{contact.email ?? '—'}</div>
        <div className="mt-2 truncate text-sm text-text-dim">{contact.company ?? '—'}</div>
        <div className="mt-3 flex flex-wrap gap-1">
          {contact.vertical && (
            <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-accent">
              {VERTICAL_LABEL[contact.vertical] ?? contact.vertical}
            </span>
          )}
          {contact.city && (
            <span className="rounded bg-surface-hi px-1.5 py-0.5 text-[10px] text-text-mute">
              {contact.city}
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}

interface StageRailProps {
  stages: Array<{ id: string; name: string; probability: number | null }>;
  pipelineLabel: string;
}

function StageRail({ stages, pipelineLabel }: StageRailProps): JSX.Element {
  return (
    <aside className="w-44 shrink-0 border-l border-border/[0.08] bg-surface/40 overflow-y-auto px-2 py-3">
      <div className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-mute">
        Drop to create deal
      </div>
      <div className="px-2 mb-3 text-[11px] text-text-dim font-mono">{pipelineLabel}</div>
      <div className="flex flex-col gap-1.5">
        {stages.map((stage) => (
          <StageDropZone key={stage.id} id={stage.id} name={stage.name} probability={stage.probability} />
        ))}
      </div>
    </aside>
  );
}

function StageDropZone({ id, name, probability }: { id: string; name: string; probability: number | null }): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: `stage:${id}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded border border-dashed px-2 py-1.5 text-xs transition',
        isOver
          ? 'border-accent/70 bg-accent/15 text-accent'
          : 'border-border/[0.16] bg-surface-2 text-text-dim hover:border-accent/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate">{name}</span>
        {probability != null && (
          <span className="font-mono text-[10px] text-text-mute shrink-0">{probability}%</span>
        )}
      </div>
    </div>
  );
}
